import React, { useEffect, useMemo, useState } from "react";
import { FaShoppingBag, FaRupeeSign, FaClock, FaSearch, FaDownload } from "react-icons/fa";
import { get } from "../../lib/http";
import { API } from "../../config/api";
import { AD } from "../adminUi.js";

const cn = (...a) => a.filter(Boolean).join(" ");

/* ---------- small helpers ---------- */
const fmtInr = (n = 0) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

const statusChip = (s = "") => {
  const k = s.toLowerCase();
  if (k.includes("paid") || k.includes("success")) return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
  if (k.includes("pending")) return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
  if (k.includes("failed") || k.includes("cancel")) return "bg-rose-50 text-rose-700 ring-1 ring-rose-200";
  return "bg-slate-50 text-slate-600 ring-1 ring-slate-200";
};

const SkeletonRow = () => (
  <tr className="border-t animate-pulse">
    <td className="py-3"><div className="h-4 w-24 bg-slate-200 rounded" /></td>
    <td className="py-3"><div className="h-4 w-20 bg-slate-200 rounded" /></td>
    <td className="py-3"><div className="h-4 w-40 bg-slate-200 rounded" /></td>
    <td className="py-3"><div className="h-4 w-10 bg-slate-200 rounded" /></td>
    <td className="py-3"><div className="h-4 w-16 bg-slate-200 rounded" /></td>
    <td className="py-3"><div className="h-6 w-20 bg-slate-200 rounded-full" /></td>
  </tr>
);

export default function AdminOrdersList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // ui state
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
  const [totalCount, setTotalCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };

        // --- Stats ---
        const statsResp = await get(`${API.booking.getAllOrders}`, { headers });

        setRows(statsResp.data || []);
        setTotalCount(statsResp.count);
        setTotalRevenue(statsResp.revenue);
        setPendingCount(statsResp.pendingCount);
      } catch {
        if (on) setRows([]);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, []);

  const filtered = useMemo(() => {
    let list = [...rows];
    // search across id, user mobile/email, event title
    const text = q.trim().toLowerCase();
    if (text) {
      list = list.filter((r) => {
        const id = String(r.id || r._id || "").toLowerCase();
        const mobile = String(r.user?.mobile || "").toLowerCase();
        const email = String(r.user?.email || "").toLowerCase();
        const event = String(r.event?.title || r.event?.name || "").toLowerCase();
        return id.includes(text) || mobile.includes(text) || email.includes(text) || event.includes(text);
      });
    }
    if (status !== "all") {
      list = list.filter((r) => (r.status || "").toLowerCase().includes(status));
    }
    // sort
    list.sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      const ka = sort.key === "amount" ? Number(a.amount || 0) :
        sort.key === "qty" ? Number(a.qty || 0) :
          sort.key === "createdAt" ? new Date(a.createdAt || a.created_at || 0).getTime() :
            String(a[sort.key] ?? "").toLowerCase();
      const kb = sort.key === "amount" ? Number(b.amount || 0) :
        sort.key === "qty" ? Number(b.qty || 0) :
          sort.key === "createdAt" ? new Date(b.createdAt || b.created_at || 0).getTime() :
            String(b[sort.key] ?? "").toLowerCase();
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
    return list;
  }, [rows, q, status, sort]);

  const stats = useMemo(() => {
    const total = rows.length;
    const revenue = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
    const pending = rows.filter((r) => String(r.status || "").toLowerCase().includes("pending")).length;
    return { total, revenue, pending };
  }, [rows]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const exportCSV = () => {
    const headers = ["Order ID", "Created At", "User mobile", "User Email", "Event", "Qty", "Amount", "Status"];
    const lines = filtered.map((r) => [
      r.bookingCode || r.bookingCode || "",
      new Date(r.createdAt || r.created_at || "").toISOString(),
      r.userId?.mobile || "",
      r.userId?.email || "",
      r.eventId?.title || r.eventId?.name || "",
      r.ticketCategories?.[0]?.finalQuantity || 0,
      r.finalAmount || 0,
      r.bookingStatus || "",
    ]);
    const csv = [headers, ...lines].map((arr) =>
      arr.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(",")
    ).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_${new Date().toISOString().slice(0, 19).replaceAll(":", "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={cn(AD.formWrapWide, AD.page)}>
      <div className={AD.headerRow}>
        <div>
          <nav className="mb-1 flex items-center gap-1.5 text-xs text-slate-500">
            <span>Admin</span>
            <span>/</span>
            <span className="font-medium text-slate-700">Orders</span>
          </nav>
          <h1 className={AD.title}>Orders</h1>
          <p className={AD.subtitle}>Recent ticket purchases and booking status.</p>
        </div>
        <button type="button" onClick={exportCSV} className={cn(AD.btnSecondary, "gap-2")}>
          <FaDownload className="text-xs" />
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={FaShoppingBag} label="Total orders" value={totalCount} tone="blue" />
        <StatCard icon={FaRupeeSign} label="Total revenue" value={fmtInr(totalRevenue)} tone="green" />
        <StatCard icon={FaClock} label="Pending" value={pendingCount} tone="amber" />
      </div>

      <div className={AD.filterCard}>
        <div className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-8">
            <label className={AD.fieldLabel}>Search</label>
            <div className="relative mt-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Order ID, user, event…"
                className={cn(AD.inputField, "pl-10")}
              />
            </div>
          </div>
          <div className="md:col-span-4">
            <label className={AD.fieldLabel}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={cn(AD.select, "mt-1")}
            >
              <option value="all">All statuses</option>
              <option value="success">Success</option>
              <option value="Confirmed">Confirmed</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="cancel">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={AD.tableShell}>
        <div className="max-w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={`${AD.thead} !bg-gradient-to-r !from-slate-50 !via-white !to-amber-50/50`}>
              <tr className="text-left text-slate-500">
                <Th onClick={() => toggleSort("createdAt")} active={sort.key === "createdAt"} dir={sort.dir}>Created</Th>
                <Th onClick={() => toggleSort("id")} active={sort.key === "id"} dir={sort.dir}>Order</Th>
                <th className="py-3 px-4">User</th>
                <th className="py-3 px-4">Event</th>
                <Th onClick={() => toggleSort("qty")} active={sort.key === "qty"} dir={sort.dir} align="right">Qty</Th>
                <Th onClick={() => toggleSort("amount")} active={sort.key === "amount"} dir={sort.dir} align="right">Amount</Th>
                <th className="py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}

              {!loading && filtered.map((r) => (
                <tr key={r.id || r._id} className="border-t hover:bg-slate-50/60 transition-colors">
                  <td className="py-3 px-4 text-slate-600">{fmtDate(r.createdAt || r.created_at)}</td>
                  <td className="py-3 px-4">
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[12px]">{r.bookingCode || r._id}</code>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-slate-900">{r.userId.name || r.userId?.mobile || r.userId?.email || "—"}</div>
                    {/* {r.userId?.email && r.userId?.mobile && r.userId.name && (
                      <div className="text-[12px] text-slate-500">{r.userId.email}</div>
                    )} */}
                  </td>
                  <td className="py-3 px-4">
                    <div className="line-clamp-2 max-w-[320px]">{r.eventId?.title || r.eventId?.name || "—"}</div>
                  </td>
                  <td className="py-3 px-4 text-right">{r.ticketCategories?.[0]?.finalQuantity ?? "—"}</td>
                  <td className="py-3 px-4 text-right">{fmtInr(r.finalAmount)}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusChip(r.status)}`}>
                      {r.bookingStatus || "—"}
                    </span>
                  </td>
                </tr>
              ))}

              {!loading && !filtered.length && (
                <tr>
                  <td colSpan="7" className="py-16 text-center">
                    <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-slate-100" />
                    <div className="text-slate-900 font-semibold">No orders found</div>
                    <div className="text-sm text-slate-500">Try adjusting filters or search</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* footer count */}
      {!loading && (
        <div className="text-xs text-slate-500">
          Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of{" "}
          <span className="font-semibold text-slate-700">{rows.length}</span> orders
        </div>
      )}
    </div>
  );
}

/* ---------- small UI bits ---------- */

function StatCard({ icon: Icon, label, value, tone = "slate" }) {
  const iconBg = {
    slate: "from-slate-400 to-slate-600",
    amber: "from-amber-400 to-orange-500",
    green: "from-emerald-400 to-emerald-600",
    blue: "from-sky-400 to-blue-600",
  };
  return (
    <div className={cn(AD.statCard, "flex items-start gap-3")}>
      {Icon && (
        <span
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm",
            iconBg[tone] || iconBg.slate
          )}
        >
          <Icon className="text-sm" />
        </span>
      )}
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="truncate text-xl font-bold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function Th({ children, onClick, active, dir, align = "left" }) {
  return (
    <th
      className={`py-3 px-4 select-none cursor-pointer ${align === "right" ? "text-right" : "text-left"}`}
      onClick={onClick}
      title="Sort"
    >
      <span className={`inline-flex items-center gap-1 ${active ? "text-slate-900 font-semibold" : ""}`}>
        {children}
        <span className={`text-[10px] ${active ? "opacity-80" : "opacity-30"}`}>
          {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </span>
    </th>
  );
}
